
import { defineComponent, ref, reactive, computed } from "vue";
import Step from "@/components/shared/Step.vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { mapActions, mapState, useStore } from "vuex";
import useValidation from "@/use/validation";
import type { FormSchema } from "@/models/form";
import { $api } from "@/services/api";
import type { EnrollmentTerm } from "@/models/school";
import useFormHelpers from "@/use/formHelpers";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Modal from "@/components/shared/Modal.vue";
import useApplicationData from "@/composables/useApplicationData";
import { compareMonthAndYear, createUTCDate } from "@/use/dateHelpers";
import useSchoolConfirmationForm from "@/composables/forms/useSchoolConfirmationForm";

export default defineComponent({
  setup() {
    const store = useStore();
    const { validateForm } = useValidation();
    const { months, years } = useFormHelpers();
    const { isUGLOC } = useApplicationData();

    const schemaRequired = computed(() => !isUGLOC.value);

    const schema = reactive({
      schoolUndecided: {
        type: "radio",
        label: "",
        hideLabel: false,
        required: false,
        errorMessage: "Please select yes or no",
        tooltipLocation: "bottom",
      },
      school: {
        name: "school",
        type: "autocomplete",
        label: "School Name",
        required: schemaRequired.value,
        autocompleteProperty: "schoolName",
      },
      academicTermId: {
        name: "academicTermId",
        type: "select",
        label: "Academic Term",
        required: schemaRequired.value,
      },
      enrollmentStatusId: {
        name: "enrollmentStatusId",
        type: "select",
        label: "Enrollment Status",
        tooltipLocation: "bottom",
        required: schemaRequired.value,
      },
      graduationDateMonth: {
        name: "graduationDateMonth",
        type: "select",
        label: "Graduation Date",
        required: true,
        errorMessage: "Grad month is required.",
      },
      graduationDateYear: {
        name: "graduationDateYear",
        type: "select",
        required: true,
        errorMessage: "Grad year is required.",
      },
    } as FormSchema);

    const error = ref<boolean>(false);
    const errorMessage = ref<string>("");
    const savingChanges = ref<boolean>(false);

    let schoolInfo = ref({ school: {} } as any);

    const showEnrollmentTerms = ref<boolean>(
      store?.state?.appData?.application.schoolConfiguration?.schoolId > 0,
    );

    const {
      academicTermOptions,
      anticipatedGraduationDate,
      enrollmentStatusChanged,
      enrollmentTermChanged,
      enrollmentStatuses,
      enrollmentStatusOptions,
      gradDateChanged,
      hasChanges,
      invalidEnrollmentTerm,
      isValidAcademicTerm,
      loanPeriodEndDate,
      originalGradDateInfo,
      previouslySelectedEnrollmentStatus,
      previouslySelectedEnrollmentTerm,
      previouslySelectedSchool,
      schoolChanged,
      selectedEnrollmentStatus,
			selectedEnrollmentTerm,
      selectedGraduationDate,
      selectedSchool,
      updatedGradDateInfo,
      validEnrollmentTerm,
    } = useSchoolConfirmationForm(schoolInfo);

    async function getData() {
      if ((store.state.schools || []).length <= 0) {
        const res = await $api.school.getSchoolsByProgram(
          store?.state?.appData?.application?.programTypeId,
          store?.state?.appData?.application?.productTypeId,
          store?.state?.appData?.application?.lenderId,
        );
        store.dispatch("setSchools", res.schools);
      }
    }

    return {
      academicTermOptions,
      anticipatedGraduationDate,
      compareMonthAndYear,
      createUTCDate,
      enrollmentStatusChanged,
      enrollmentStatuses,
      enrollmentStatusOptions,
      enrollmentTermChanged,
      error,
      errorMessage,
      getData,
      gradDateChanged,
      hasChanges,
      invalidEnrollmentTerm,
      isUGLOC,
      isValidAcademicTerm,
      loanPeriodEndDate,
      months,
      schema,
      store,
      schoolInfo,
      originalGradDateInfo,
      previouslySelectedEnrollmentStatus,
      previouslySelectedEnrollmentTerm,
      previouslySelectedSchool,
      schoolChanged,
      selectedEnrollmentStatus,
      selectedEnrollmentTerm,
      selectedGraduationDate,
      selectedSchool,
      showEnrollmentTerms,
      schemaRequired,
      savingChanges,
      updatedGradDateInfo,
      validateForm,
      validEnrollmentTerm,
      years,
    };
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
    applicant: {
      type: Object,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isCurrent: {
      type: Boolean,
      required: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
    },
    term: {
      type: String,
    },
    isCurrentSubStep: {
      type: Boolean,
      default: false,
    },
  },
  components: { Step, BaseFormField, CircleLoader, NextButton, Modal },
  emits: ["previous", "close", "complete"],
  data: () => ({
    path: "",
    gettingData: true,
    showConfirmationQuestion: undefined as boolean | undefined,
    updatedSchoolInfo: { school: {} } as any,
    schoolPlanOptions: [
      {
        label: "Yes",
        value: false,
      },
      {
        label: "No",
        value: true,
      },
    ],
  }),
  computed: {
    ...mapState(["appData", "schools"]),
  },
  watch: {
    showConfirmationQuestion(value) {
      this.schema.schoolUndecided.required = value;
    },
    "schoolInfo.schoolUndecided": {
      handler(newVal, oldVal) {
        this.initializeModalStyle();

        if (newVal === true) {
          if (this.schoolInfo.school && this.schoolInfo?.school?.id > 0) {
            const ref = this.$refs["schoolSearchRef"] as InstanceType<typeof BaseFormField>;

            if (ref) {
              ref.autocompleteSearch = "";
            }
          }
          // clear out the selection
          this.schoolInfo.academicTermId = undefined;
          this.schoolInfo.school = { id: 0 };
          this.schema.school.required = false;
          this.schema.academicTermId.required = false;
          this.schema.enrollmentStatusId.required = false;
        } else {
          this.schema.school.required = true;
          this.schema.academicTermId.required = true;
          this.schema.enrollmentStatusId.required = true;
        }
      },
    },
  },
  methods: {
    ...mapActions(["setSchools", "setAppData"]),
    async initialize() {
      await this.getData();
    },
    async setSchoolInfo() {
      this.gettingData = true;

      await this.initialize();

      // For LOSCore
      if (!this.appData.application.schoolConfiguration) {
        this.appData.application.schoolConfiguration = {};
      }

      this.schoolInfo = {
        school: this.previouslySelectedSchool || {},
      };

      this.schoolInfo.schoolUndecided =
        this.appData.application.schoolConfiguration.schoolUndecided;

      // Handle school undecided flow
      if (
        this.appData?.application?.schoolConfiguration?.schoolId &&
        this.appData?.application?.schoolConfiguration?.schoolId > 0
      ) {
        this.schoolInfo.schoolUndecided = false;
      }

      if (this.appData?.application?.schoolConfiguration?.graduationDate) {
        const date = this.createUTCDate(
          this.appData.application.schoolConfiguration.graduationDate,
        );
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        const day = date.getUTCDate();
        this.schoolInfo.graduationDateDay = day;
        this.schoolInfo.graduationDateMonth = month;
        this.schoolInfo.graduationDateYear = year;
        this.schoolInfo.graduationDate = date;
      }

      await this.handleSchoolSelection();

      if (this.appData.application.schoolConfiguration?.academicTerm) {
        this.schoolInfo.academicTerm = this.appData?.application?.schoolConfiguration?.academicTerm;
      }

      if (this.appData.application.schoolConfiguration?.academicTermId) {
        this.schoolInfo.academicTermId =
          this.appData?.application?.schoolConfiguration?.academicTermId;
      }

      if (this.appData.application.schoolConfiguration?.enrollmentStatus) {
        this.schoolInfo.enrollmentStatusId = this.previouslySelectedEnrollmentStatus?.value;
      }

      // Story 4063: No should be selected by default if they answered "No" on the initial School Plans page
      this.showConfirmationQuestion = this.schoolInfo.schoolUndecided;

      this.gettingData = false;
    },
    async handleSchoolSelection() {
      if (this.schoolInfo.school?.id && this.schoolInfo.school?.id > 0) {
        await this.store.dispatch("getEnrollmentTerms", this.schoolInfo.school.id ?? 0);
        this.showEnrollmentTerms = true;
      }
    },
    setPath(path: string) {
      this.path = path;
    },
    setSchoolUndecidedLabel(path: string) {
      if (path === "Primary Applicant") {
        this.schema.schoolUndecided.label = "Have you confirmed the school you will be attending?";
      } else if (path === "Co-Applicant") {
        this.schema.schoolUndecided.label =
          "Have you confirmed the school your student will be attending?";
      } else {
        ("Have you confirmed the school your student will be attending?");
      }
    },
    setAlreadyChosenPath(applicantType: any) {
      // The user has already chosen a value from the ChoosePath
      switch (applicantType) {
        case 1:
        case "Primary Applicant":
          this.setPath("Primary Applicant");
          break;
        case 2:
        case "Co-Applicant":
          this.setPath("Co-Applicant");
          break;
        default:
      }

      this.setSchoolUndecidedLabel(this.path);
    },
    goBackToPortal() {
      this.savingChanges = true;
      const valid = this.validateForm(this.schema, this.schoolInfo);
      if (valid) {
        if (!this.appData.application.schoolConfiguration) {
          this.appData.application.schoolConfiguration = {};
        }

        const enrollmentTerm = this.store.state.enrollmentTerms.find(
          (p: EnrollmentTerm) => p.id == this.schoolInfo.academicTermId,
        );

        if (enrollmentTerm && this.loanPeriodEndDate && this.selectedGraduationDate) {
          const endDate = new Date(this.loanPeriodEndDate);

          const validGradDate = this.isValidAcademicTerm(this.selectedGraduationDate, endDate);

          if (validGradDate) {
            this.invalidEnrollmentTerm = false;
            this.schoolInfo.graduationDate = this.selectedGraduationDate;
            this.schoolInfo.enrollmentTerm = enrollmentTerm;
            this.schoolInfo.academicTerm = enrollmentTerm.description;
            this.schoolInfo.academicTermId = enrollmentTerm.id;
            this.schoolInfo.selectedTerm = enrollmentTerm.description;
            this.schoolInfo.hasSchoolConfigChanges = this.hasChanges;

            this.$emit("complete", this.schoolInfo);
          } else {
            this.invalidEnrollmentTerm = true;
            this.savingChanges = false;
          }
        } else {
          this.schoolInfo.graduationDate = this.selectedGraduationDate;
					this.schoolInfo.hasSchoolConfigChanges = this.gradDateChanged;

          this.$emit("complete", this.schoolInfo);
        }
      } else {
        this.savingChanges = false;
      }
    },
    initializeModalStyle() {
      if (
        this.$refs["stepRef"] &&
        (this.$refs["stepRef"] as InstanceType<typeof Step>) &&
        (this.$refs["stepRef"] as InstanceType<typeof Step>) !== null &&
        (this.$refs["stepRef"] as InstanceType<typeof Step>)?.stepContentRef != null &&
        (this.$refs["stepRef"] as InstanceType<typeof Step>)?.stepContentRef
      ) {
        const stepContentRef = (this.$refs["stepRef"] as InstanceType<typeof Step>)
          .stepContentRef as HTMLElement;

        const stepButtonsRef = (this.$refs["stepRef"] as InstanceType<typeof Step>)
          .stepButtonsRef as HTMLElement;

        stepContentRef.style.overflowY = "auto";
        stepContentRef.style.minHeight = "300px";
        stepContentRef.style.marginBottom = "1rem";

        if (window.innerWidth <= 992) {
          stepContentRef.style.minHeight = "calc(100% + 200px)";
          stepButtonsRef.style.paddingBottom = "0px";
        }

        if (this.isUGLOC && this.schoolInfo.schoolUndecided) {
          stepContentRef.style.minHeight = "100%";
          stepContentRef.style.marginBottom = "2rem";
        }
      }
    },
  },
  async mounted() {
    await this.setAlreadyChosenPath(this.appData?.currentApplicant?.applicantTypeName);
    await this.getData();
    await this.setSchoolInfo();

    this.initializeModalStyle();
  },
});
